// PUBLIC visitor / cartage / subcontractor site induction — /induct/:token
// No login. Scanned from the site QR poster. Read → knowledge check → declaration → sign.
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SignaturePad } from "@/components/SignaturePad";
import {
  Loader2, ShieldCheck, MapPin, Flame, AlertTriangle, HardHat, ClipboardList,
  Truck, Leaf, Megaphone, CheckCircle2, PenLine, Info,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { welcome: Info, emergency: Flame, site_rules: ClipboardList, hazards: AlertTriangle, ppe: HardHat, plant_traffic: Truck, hazardous_substances: ShieldCheck, environment: Leaf, reporting: Megaphone };
const LABELS = { welcome: "Welcome", emergency: "Emergency", site_rules: "Site Rules", hazards: "Hazards & Controls", ppe: "Required PPE", plant_traffic: "Plant & Traffic", hazardous_substances: "Hazardous Substances", environment: "Environment", reporting: "Reporting" };
const FIELD_LABELS = { body: "", scope: "Scope of works", access: "Site access", parking: "Parking", restricted_areas: "Restricted areas", emergency_contact: "Emergency contact", muster_point: "Muster point", first_aid_location: "First aid", first_aiders: "First aiders", fire_equipment: "Fire equipment", emergency_access: "Emergency access", nearest_medical: "Nearest medical", evacuation: "Evacuation" };
const PTYPES = [["visitor", "Visitor"], ["cartage", "Cartage / delivery driver"], ["subcontractor", "Subcontractor"]];

const hasContent = (k, v) => (k === "ppe" || k === "site_rules" || k === "hazards")
  ? Array.isArray(v) && v.length > 0
  : v && typeof v === "object" && Object.values(v).some((x) => String(x || "").trim());

export default function PublicInduction() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [ptype, setPtype] = useState("visitor");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [answers, setAnswers] = useState({});
  const [accepted, setAccepted] = useState(false);
  const [signing, setSigning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let ok = true;
    axios.get(`${API}/site-inductions/public/${token}`)
      .then((r) => { if (ok) setData(r.data); })
      .catch((e) => ok && setErr(e?.response?.data?.detail || "This induction link isn't active"))
      .finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [token]);

  const content = data?.revision?.content || {};
  const kc = content.knowledge_check || {};
  const questions = kc.enabled ? (kc.questions || []) : [];
  const declaration = (content.declaration || {}).text || "";
  const visibleSections = useMemo(() => ["welcome", "emergency", "site_rules", "hazards", "ppe", "plant_traffic", "environment", "reporting"].filter((k) => hasContent(k, content[k])), [content]);

  const submit = async (signature) => {
    setSigning(false);
    if (!signature) return;
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    if (!accepted) { toast.error("Please tick the declaration first"); return; }
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/site-inductions/public/${token}/complete`, {
        signature, declaration_accepted: true, participant_type: ptype,
        participant_name: name.trim(), company: company.trim() || undefined, phone: phone.trim() || undefined,
        answers: questions.map((_, i) => answers[i] || ""),
      });
      setDone(res);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit induction");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-zinc-50"><Loader2 className="w-7 h-7 animate-spin text-[#FF5F15]" /></div>;
  if (err) return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 px-6 text-center">
      <div><AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" /><h1 className="text-xl font-black">Induction unavailable</h1><p className="text-sm text-zinc-500 mt-1">{err}</p></div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 px-6 text-center" data-testid="pub-induct-done">
      <div className="max-w-md">
        <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4"><CheckCircle2 className="w-9 h-9 text-emerald-600" /></div>
        <h1 className="text-2xl font-black tracking-tight">You're inducted</h1>
        <p className="text-sm text-zinc-500 mt-1">{data.induction.site_name} · {data.induction.job_number}</p>
        {done.knowledge_result && <p className="text-sm mt-3 font-bold">Knowledge check: {done.knowledge_result.correct}/{done.knowledge_result.total} acknowledged</p>}
        <p className="text-xs text-zinc-400 mt-4">Thanks {done.name}. Your signed induction has been recorded for this site. Follow the site rules, wear your PPE and report any hazards to the supervisor.</p>
        <p className="text-[11px] text-zinc-400 mt-6">Powered by Fieldlink</p>
      </div>
    </div>
  );

  if (!data) return null;

  return (
    <div className="min-h-screen bg-zinc-50 pb-16" data-testid="pub-induct">
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <div className="rounded-2xl bg-[#09090B] text-white p-6 mb-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF5F15] mb-1">Site Induction</div>
          <h1 className="text-2xl font-black tracking-tight leading-tight">{data.induction.site_name}</h1>
          <div className="text-sm text-zinc-300 mt-1">{data.induction.job_number}{data.induction.client ? ` · ${data.induction.client}` : ""}</div>
          <div className="text-[11px] text-zinc-400 mt-2">{data.org_name} · REV {String(data.revision.revision_number).padStart(2, "0")}</div>
        </div>

        <SiteInfoCard info={content.site_information} />
        {visibleSections.map((k) => <SectionCard key={k} sectionKey={k} value={content[k]} />)}

        {questions.length > 0 && (
          <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-4" data-testid="pub-induct-kc">
            <h2 className="font-black text-lg mb-1 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#FF5F15]" /> Knowledge check</h2>
            <p className="text-xs text-zinc-400 mb-4">Answer to confirm you understand the key points.</p>
            <div className="space-y-4">
              {questions.map((q, i) => (
                <div key={i} data-testid={`pub-induct-q-${i}`}>
                  <label className="text-sm font-bold block mb-1.5">{i + 1}. {q.q}</label>
                  <Input value={answers[i] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))} placeholder="Your answer" className="rounded-lg h-11" data-testid={`pub-induct-a-${i}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-4" data-testid="pub-induct-details">
          <h2 className="font-black text-lg mb-3">Your details</h2>
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1.5">I am a…</label>
          <div className="flex flex-wrap gap-2 mb-4">
            {PTYPES.map(([v, label]) => (
              <button key={v} onClick={() => setPtype(v)} data-testid={`pub-induct-ptype-${v}`}
                className={`px-3 py-2 rounded-lg text-sm font-bold border ${ptype === v ? "bg-[#FF5F15] text-white border-[#FF5F15]" : "bg-white text-zinc-600 border-zinc-200"}`}>{label}</button>
            ))}
          </div>
          <div className="space-y-3">
            <div><label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Full name *</label><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-lg h-11" data-testid="pub-induct-name" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Company</label><Input value={company} onChange={(e) => setCompany(e.target.value)} className="rounded-lg h-11" data-testid="pub-induct-company" /></div>
              <div><label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Phone</label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-lg h-11" data-testid="pub-induct-phone" /></div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-4" data-testid="pub-induct-declaration">
          <h2 className="font-black text-lg mb-3 flex items-center gap-2"><PenLine className="w-5 h-5 text-[#FF5F15]" /> Declaration</h2>
          <p className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{declaration}</p>
          <label className="flex items-start gap-2.5 mt-4 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FF5F15]" data-testid="pub-induct-accept" />
            <span>I confirm I have read and understood this induction and agree to the declaration above.</span>
          </label>
          <div className="mt-5">
            {signing ? (
              <SignaturePad onSave={submit} onCancel={() => setSigning(false)} testIdPrefix="pub-induct-sig" />
            ) : (
              <Button onClick={() => { if (!name.trim()) return toast.error("Please enter your name"); if (!accepted) return toast.error("Please tick the declaration first"); setSigning(true); }}
                disabled={busy} className="w-full h-12 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white font-black uppercase tracking-wider text-sm" data-testid="pub-induct-sign">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><PenLine className="w-4 h-4 mr-2" /> Sign & complete induction</>}
              </Button>
            )}
          </div>
        </div>
        <p className="text-center text-[11px] text-zinc-400">Powered by Fieldlink</p>
      </div>
    </div>
  );
}

function SiteInfoCard({ info }) {
  if (!info) return null;
  const rows = [["Site", info.site_name], ["Address", info.site_address], ["Client", info.client], ["Project manager", info.project_manager], ["Site supervisor", info.site_supervisor], ["Working hours", info.working_hours]].filter(([, v]) => String(v || "").trim());
  if (!rows.length) return null;
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-4" data-testid="pub-induct-siteinfo">
      <h2 className="font-black text-lg mb-3 flex items-center gap-2"><MapPin className="w-5 h-5 text-[#FF5F15]" /> Site information</h2>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {rows.map(([k, v]) => <div key={k}><dt className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{k}</dt><dd className="font-medium">{v}</dd></div>)}
      </dl>
    </div>
  );
}

function SectionCard({ sectionKey, value }) {
  const Icon = ICONS[sectionKey] || Info;
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-4" data-testid={`pub-induct-sec-${sectionKey}`}>
      <h2 className="font-black text-lg mb-3 flex items-center gap-2"><Icon className="w-5 h-5 text-[#FF5F15]" /> {LABELS[sectionKey]}</h2>
      {sectionKey === "ppe" ? (
        <div className="flex flex-wrap gap-2">{(value || []).map((p, i) => <span key={i} className="px-3 py-1.5 rounded-full bg-[#FF5F15]/10 text-[#E65100] text-sm font-bold">{p}</span>)}</div>
      ) : sectionKey === "site_rules" ? (
        <ul className="space-y-2">{(value || []).map((r, i) => <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {r.title || r}</li>)}</ul>
      ) : sectionKey === "hazards" ? (
        <div className="space-y-3">{(value || []).map((h, i) => <div key={i} className="rounded-lg border border-amber-200 bg-amber-50/60 p-3"><div className="font-bold text-sm text-amber-900 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {h.name}</div>{h.controls && <div className="text-sm text-zinc-700 mt-1">{h.controls}</div>}</div>)}</div>
      ) : (
        <div className="space-y-2.5">{Object.entries(value || {}).filter(([, v]) => String(v || "").trim()).map(([k, v]) => (
          <div key={k}>{FIELD_LABELS[k] ? <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-0.5">{FIELD_LABELS[k]}</div> : null}<div className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{v}</div></div>
        ))}</div>
      )}
    </div>
  );
}
